import { CRATE_FRICTION, PLAYER_ACCEL, PLAYER_FRICTION, PLAYER_MAX_SPEED } from './constants';
import { stepNpcBoxes } from './npcBoxes';
import { collectNpcTouches, stepNpcs, stepNpcTouches } from './npcs';
import { stepChess } from './chess';
import { stepMatchEnd, stepPhase } from './match';
import { accelerate, applyFriction, moveBody, resolveBodies } from './physics';
import { cloneState } from './state';
import type { Body, ChessMove, Input, Inputs, PlayerId, PromotionPiece, Square, State } from './types';
import { clampLen, sanitize } from './vec';
import { fire, stepBeams, stepBlasts, stepBursts, stepPickups, stepProjectiles, stepTimers } from './weapons';

const PLAYER_IDS: readonly PlayerId[] = [0, 1];

const SQUARE = /^[a-h][1-8]$/;
const PROMOTIONS: readonly string[] = ['q', 'r', 'b', 'n'];

/**
 * A chess move only in the exact shape {from, to, promotion?}; anything else is no move. The server's
 * wire check uses it too, so what the room buffers is already in this shape.
 */
export function sanitizeChessMove(move: unknown): ChessMove | undefined {
  if (typeof move !== 'object' || move === null) return undefined;
  const m = move as Record<string, unknown>;
  if (typeof m.from !== 'string' || !SQUARE.test(m.from) || typeof m.to !== 'string' || !SQUARE.test(m.to)) return undefined;
  const clean: ChessMove = { from: m.from as Square, to: m.to as Square };
  if (m.promotion !== undefined) {
    if (typeof m.promotion !== 'string' || !PROMOTIONS.includes(m.promotion)) return undefined;
    clean.promotion = m.promotion as PromotionPiece;
  }
  return clean;
}

/** Inputs come from the network: never trust their shape. */
function sanitizeInput(input: Input | undefined): Input {
  return {
    seq: input?.seq ?? 0,
    moveDir: clampLen(sanitize(input?.moveDir), 1),
    fireAt: input?.fireAt ? sanitize(input.fireAt) : undefined,
    chessMove: sanitizeChessMove(input?.chessMove),
  };
}

/**
 * Advances the match by one tick. Pure: the given state is not touched, a new one is returned.
 * A finished match only counts ticks. Transitions count ticks but freeze gameplay. Order: phase → shooter
 * (timers → boxes → inputs → NPCs → bursts → bodies → projectiles → beams → blasts → NPC touches → pickups)
 * or chess (move → clock) → match end.
 */
export function step(state: State, inputs: Inputs): State {
  const s = cloneState(state);
  if (s.winner === null) {
    stepPhase(s);
    if (s.transitionTicks > 0) {
      s.transitionTicks--;
      s.phaseEndsAtTick++;
      s.chess.lastMoveTick++;
      s.nextNpcBoxTick++;
      for (const entry of s.spawnPlan) entry.atTick++;
      for (const burst of s.bursts) burst.nextTick++;
      s.tick++;
      return s;
    }
    const clean: Inputs = { 0: sanitizeInput(inputs[0]), 1: sanitizeInput(inputs[1]) };
    if (s.phase === 'shooter') stepShooter(s, clean);
    else stepChess(s, clean);
    stepMatchEnd(s);
  }
  s.tick++;
  return s;
}

function stepShooter(s: State, inputs: Inputs): void {
  stepTimers(s);
  stepNpcBoxes(s);
  for (const id of PLAYER_IDS) {
    const player = s.players[id];
    const input = inputs[id];
    accelerate(player, input.moveDir, PLAYER_ACCEL, PLAYER_FRICTION, PLAYER_MAX_SPEED);
    applyFriction(player, 1);
    if (input.fireAt) fire(s, player, input.fireAt);
  }
  stepNpcs(s);
  // after the players' friction, like fire(): a round's recoil then slides the same distance either way
  stepBursts(s);
  for (const crate of s.crates) applyFriction(crate, CRATE_FRICTION);

  const bodies: Body[] = [...s.players, ...s.crates, ...s.npcs];
  const obstacles = [...s.pillars, ...s.npcBoxes];
  const touchingNpcs = new Set<number>();
  collectNpcTouches(s, touchingNpcs);
  resolveBodies(s.map, obstacles, bodies);
  for (const body of bodies) moveBody(s.map, body);
  collectNpcTouches(s, touchingNpcs);
  resolveBodies(s.map, obstacles, bodies);

  stepProjectiles(s);
  stepBeams(s);
  stepBlasts(s);
  stepNpcTouches(s, touchingNpcs);
  stepPickups(s);
}
